/**
 * SecondarySpikeCard — #2 / #3 spike on the Trending Today page (bd
 * deepcontext-yx28, superseding the full-bleed gradient card from bd
 * deepcontext-d68.22).
 *
 * Page-lock 3 mockup: a compact `--surface` card stacked beside the
 * {@link HeroSpikeCard} — rank + label kicker, headline topic phrase, and
 * URL/delta metrics (delta in safe/risk ink). No sparkline — the mockup's
 * `.sside .scard` only carries the kicker, title and metric row.
 */

import React from 'react';
import type {TrendingClusterPayload} from '../../../api/clusters';
import {formatDelta} from '../utils/formatDelta';

export interface SecondarySpikeCardProps {
    cluster: TrendingClusterPayload;
    rank: number;
    selected?: boolean;
    onSelect?: (clusterId: string) => void;
    /**
     * False while `payload.snapshot_hours < 24` (bd deepcontext-ws2b.3) — see
     * {@link HeroSpikeCardProps.showGrowth}. Defaults to true.
     */
    showGrowth?: boolean;
}

const SecondarySpikeCard: React.FC<SecondarySpikeCardProps> = ({
    cluster,
    rank,
    selected,
    onSelect,
    showGrowth = true,
}) => {
    const delta = formatDelta(cluster.delta_pct);
    const headline = cluster.topics[0]?.phrase ?? cluster.label;
    const ring = selected ? 'ring-2 ring-inset ring-accent' : 'hover:shadow-sm';

    return (
        <button
            type="button"
            onClick={() => onSelect?.(cluster.id)}
            data-testid={`trending-secondary-card-${cluster.id}`}
            aria-pressed={selected ?? false}
            className={`relative w-full text-left rounded-2xl overflow-hidden p-3.5 bg-surface border border-line shadow-sm flex flex-col justify-center transition-all ${ring}`}
        >
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-accent-active mb-1 flex items-center gap-1.5">
                <span>#{rank}</span>
            </div>
            <div
                data-testid={`trending-secondary-headline-${cluster.id}`}
                className="font-bold text-sm text-ink tracking-tight leading-snug truncate"
            >
                {headline}
            </div>
            <div className="flex gap-3.5 mt-1 text-sm text-ink-muted font-semibold">
                <span>
                    <span className="font-bold text-ink tabular-nums">{cluster.current_count.toLocaleString()}</span> ad requests
                </span>
                {showGrowth && (
                    <span className={`font-bold tabular-nums ${delta.toneClass}`}>{delta.text}</span>
                )}
            </div>
        </button>
    );
};

export default SecondarySpikeCard;
